import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    Save, Eye, Pencil, PenLine, RefreshCw,
    FileText, Code, CheckSquare, BookOpen, Maximize2, Minimize2, Check, AlertCircle
} from 'lucide-react';
import { apiService, handleApiError } from '../services/api';
import Loading from './common/Loading';
import MarkdownRenderer from './common/MarkdownRenderer';
import MarkdownToolbar from './common/MarkdownToolbar';
import RefineChatPanel from './RefineChatPanel';
import usePageTitle from '../hooks/usePageTitle';
import { Breadcrumbs, Button } from './ui';
import { useConfirm } from './ui/ConfirmDialog';

export default function ConceptEditor() {
    const { courseId, domain, concept } = useParams();

    const [course, setCourse] = useState(null);
    const [content, setContent] = useState('');
    const [contentType, setContentType] = useState('material');
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [error, setError] = useState(null);
    const [previewMode, setPreviewMode] = useState(false);
    const [focusMode, setFocusMode] = useState(false);
    const [savedContent, setSavedContent] = useState('');
    const [autoSaveStatus, setAutoSaveStatus] = useState(null); // null | 'saving' | 'saved'
    const confirm = useConfirm();
    const textareaRef = useRef(null);
    const autoSaveTimer = useRef(null);

    const hasUnsavedChanges = content !== savedContent;

    usePageTitle(`${concept?.replace(/_/g, ' ')} - Editor`);

    const contentTypes = [
        { key: 'material', label: 'Material', icon: BookOpen },
        { key: 'examples', label: 'Examples', icon: Code },
        { key: 'exercises', label: 'Exercises', icon: CheckSquare },
        { key: 'guide', label: 'Guide', icon: FileText }
    ];

    useEffect(() => {
        loadData();
    }, [courseId, domain, concept, contentType]);

    useEffect(() => {
        const handleKeyPress = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 's') {
                e.preventDefault();
                saveContent();
            }
            if ((e.ctrlKey || e.metaKey) && e.key === 'p') {
                e.preventDefault();
                setPreviewMode(!previewMode);
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [content, previewMode]);

    const loadData = async () => {
        setLoading(true);
        try {
            const courseResponse = await apiService.getCourse(courseId);
            setCourse(courseResponse.data);

            try {
                const contentResponse = await apiService.getConceptContent(courseId, domain, concept, contentType);
                const c = contentResponse.data.content || '';
                setContent(c);
                setSavedContent(c);
            } catch (error) {
                setContent('');
                setSavedContent('');
            }
        } catch (error) {
            setError(handleApiError(error));
        } finally {
            setLoading(false);
        }
    };

    const generateContent = async (type = contentType) => {
        if (type === contentType && content.trim()) {
            const ok = await confirm({
                title: 'Replace this draft?',
                body: 'Generating writes a new version over the current text. Unsaved edits will be lost.',
                confirmLabel: 'Regenerate',
            });
            if (!ok) return;
        }
        setGenerating(true);
        setError(null);

        try {
            const response = await apiService.generateConceptContent({
                course_id: courseId,
                domain: domain,
                concept: concept,
                content_type: type,
                course_context: course
            });

            setContent(response.data.content);
            setError(null);
        } catch (error) {
            setError(handleApiError(error));
        } finally {
            setGenerating(false);
        }
    };

    const saveContent = useCallback(async (silent = false) => {
        if (!silent) setSaving(true);
        setError(null);

        try {
            await apiService.saveConceptContent({
                course_id: courseId,
                domain: domain,
                concept: concept,
                content_type: contentType,
                content: content
            });
            setSavedContent(content);
            if (silent) {
                setAutoSaveStatus('saved');
                setTimeout(() => setAutoSaveStatus(null), 2000);
            }
        } catch (error) {
            // Surface errors even on auto-save — silent failures led to
            // "I filled it but it shows empty" reports because users assumed
            // auto-save persisted when it actually 4xx'd.
            const msg = handleApiError(error);
            if (silent) {
                setAutoSaveStatus('error');
                setError(`Auto-save failed: ${msg}. Click Save to retry.`);
            } else {
                setError(msg);
            }
        } finally {
            if (!silent) setSaving(false);
        }
    }, [courseId, domain, concept, contentType, content]);

    // Auto-save after 3 seconds of no typing
    useEffect(() => {
        if (!hasUnsavedChanges || !savedContent && !content) return; // Don't auto-save empty initial state
        if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
        setAutoSaveStatus(null);
        autoSaveTimer.current = setTimeout(() => {
            setAutoSaveStatus('saving');
            saveContent(true);
        }, 3000);
        return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
    }, [content]);

    // Warn before leaving with unsaved changes
    useEffect(() => {
        const handler = (e) => {
            if (hasUnsavedChanges) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [hasUnsavedChanges]);

    const getDomainConfig = () => {
        return course?.domains?.find(d => d.key === domain);
    };

    const getConceptDisplay = () => {
        return concept.replace(/_/g, ' ');
    };

    if (loading) return <Loading message="Loading content editor..." />;

    if (error && !content && !savedContent) {
        return (
            <div className="min-h-screen bg-paper">
                <div className="max-w-md mx-auto pt-20 text-center">
                    <h2 className="text-xl font-semibold text-status-bad mb-4">Failed to load editor</h2>
                    <p className="text-mutedink mb-6">{error}</p>
                    <Button onClick={loadData}>Try again</Button>
                </div>
            </div>
        );
    }

    const domainConfig = getDomainConfig();
    const currentContentType = contentTypes.find(t => t.key === contentType);
    const typeLabel = currentContentType?.label.toLowerCase();
    const otherTypes = contentTypes.filter(t => t.key !== contentType);

    const saveState = saving || autoSaveStatus === 'saving'
        ? { text: 'Saving…', cls: 'text-faint' }
        : autoSaveStatus === 'error'
            ? { text: 'Not saved', cls: 'text-status-bad' }
            : hasUnsavedChanges
                ? { text: 'Unsaved changes', cls: 'text-mutedink' }
                : autoSaveStatus === 'saved'
                    ? { text: 'Saved', cls: 'text-status-good' }
                    : null;

    // Edit / Preview as one segmented control: the active mode is always visible.
    const modeToggle = (
        <div className="inline-flex rounded-lg border border-line p-0.5 bg-surface" role="tablist" aria-label="Editor mode">
            {[{ key: false, label: 'Edit', icon: Pencil }, { key: true, label: 'Preview', icon: Eye }].map(({ key, label, icon: Icon }) => (
                <button
                    key={label}
                    role="tab"
                    aria-selected={previewMode === key}
                    onClick={() => setPreviewMode(key)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                        previewMode === key ? 'bg-surface-subtle text-ink' : 'text-mutedink hover:text-ink'
                    }`}
                >
                    <Icon size={14} /> {label}
                </button>
            ))}
        </div>
    );

    return (
        <div
            className="min-h-screen bg-paper text-read transition-[padding] duration-200 ease-out"
            style={{ paddingRight: 'var(--refine-panel-width, 0px)' }}
        >
            {/* Header: where you are on the left, the page's actions on the right. */}
            {!focusMode && (
                <div className="border-b border-line bg-paper/90 backdrop-blur-sm sticky top-[65px] z-10">
                    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-3 flex items-center justify-between gap-6">
                        <Breadcrumbs
                            className="min-w-0"
                            items={[
                                { label: 'My Courses', to: '/my-courses' },
                                { label: course?.name, to: `/course/${courseId}` },
                                { label: domainConfig?.name, to: `/course/${courseId}/content/${domain}` },
                                { label: getConceptDisplay() },
                            ]}
                        />
                        <div className="flex items-center gap-3 flex-shrink-0">
                            {saveState && (
                                <span className={`text-xs flex items-center gap-1 ${saveState.cls}`} aria-live="polite">
                                    {saveState.text === 'Saved' && <Check size={13} />}
                                    {saveState.text}
                                </span>
                            )}
                            {modeToggle}
                            <button
                                onClick={() => setFocusMode(true)}
                                className="p-2 text-mutedink hover:text-ink hover:bg-surface-subtle rounded-lg transition-colors"
                                title="Focus mode"
                                aria-label="Focus mode"
                            >
                                <Maximize2 size={16} />
                            </button>
                            <Button size="sm" onClick={() => saveContent(false)} disabled={saving || !hasUnsavedChanges}>
                                <Save size={14} />
                                {saving ? 'Saving…' : 'Save'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-6xl mx-auto px-6 lg:px-10 py-8">
                <div className="flex gap-10">

                    {/* Sidebar: pick the piece, then generate it. */}
                    {!focusMode && (
                        <aside className="w-60 flex-shrink-0">
                            <div className="sticky top-[150px] space-y-8">
                                <nav aria-label="Content type">
                                    <h3 className="text-xs font-semibold text-mutedink uppercase tracking-wider mb-3">
                                        Content
                                    </h3>
                                    <div className="space-y-1">
                                        {contentTypes.map((type) => {
                                            const Icon = type.icon;
                                            const active = contentType === type.key;
                                            return (
                                                <button
                                                    key={type.key}
                                                    onClick={() => setContentType(type.key)}
                                                    aria-current={active ? 'page' : undefined}
                                                    className={`w-full text-left pl-3 pr-4 py-2.5 rounded-lg border-l-2 transition-colors flex items-center gap-3 text-sm ${
                                                        active
                                                            ? 'border-accent bg-surface text-ink font-medium shadow-sm'
                                                            : 'border-transparent text-mutedink hover:bg-surface-subtle hover:text-ink'
                                                    }`}
                                                >
                                                    <Icon size={16} />
                                                    {type.label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </nav>

                                <section className="rounded-xl border border-line bg-surface p-4 space-y-4">
                                    <div>
                                        <h3 className="text-sm font-semibold text-ink">Generate with AI</h3>
                                        <p className="text-xs text-mutedink mt-1">
                                            {content ? `Write a new version of this ${typeLabel}.` : `Draft the ${typeLabel} for this concept.`}
                                        </p>
                                    </div>
                                    <Button className="w-full" onClick={() => generateContent()} disabled={generating}>
                                        {generating ? (
                                            <>
                                                <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                Generating…
                                            </>
                                        ) : content ? (
                                            <><RefreshCw size={14} /> Regenerate {typeLabel}</>
                                        ) : (
                                            <><PenLine size={14} /> Generate {typeLabel}</>
                                        )}
                                    </Button>
                                    <div className="pt-3 border-t border-line">
                                        <p className="text-xs text-mutedink mb-2">Also generate</p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {otherTypes.map(type => (
                                                <button
                                                    key={type.key}
                                                    onClick={() => {
                                                        setContentType(type.key);
                                                        setTimeout(() => generateContent(type.key), 100);
                                                    }}
                                                    disabled={generating}
                                                    className="px-2.5 py-1 text-xs rounded-md border border-line text-read hover:border-line-strong hover:text-ink transition-colors disabled:opacity-50"
                                                >
                                                    {type.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </aside>
                    )}

                    {/* Main column */}
                    <main className="flex-1 min-w-0">
                        {focusMode && (
                            <div className="flex items-center justify-between mb-8 pb-6 border-b border-line">
                                <div>
                                    <h2 className="text-2xl font-semibold text-ink mb-1">{getConceptDisplay()}</h2>
                                    <p className="text-sm text-mutedink">{currentContentType?.label}</p>
                                </div>
                                <div className="flex items-center gap-3">
                                    {modeToggle}
                                    <button
                                        onClick={() => setFocusMode(false)}
                                        className="p-2 text-mutedink hover:text-ink hover:bg-surface-subtle rounded-lg transition-colors"
                                        aria-label="Exit focus mode"
                                    >
                                        <Minimize2 size={18} />
                                    </button>
                                </div>
                            </div>
                        )}

                        {!focusMode && (
                            <div className="flex items-baseline justify-between mb-4">
                                <h1 className="text-lg font-semibold text-ink">{currentContentType?.label}</h1>
                                {content && (
                                    <span className="text-xs text-faint">{content.length.toLocaleString()} characters</span>
                                )}
                            </div>
                        )}

                        {error && (
                            <div className="flex items-start gap-2 bg-[var(--error-soft)] border border-line rounded-lg px-4 py-3 mb-4">
                                <AlertCircle size={16} className="text-status-bad mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-status-bad">{error}</p>
                            </div>
                        )}

                        <div className={focusMode ? 'max-w-3xl mx-auto' : ''}>
                            {previewMode ? (
                                <div className="rounded-xl border border-line bg-surface px-8 py-10 min-h-[600px]">
                                    {content ? (
                                        <article className="prose max-w-3xl mx-auto text-read">
                                            <MarkdownRenderer content={content} />
                                        </article>
                                    ) : (
                                        <div className="text-center py-32">
                                            <FileText className="w-10 h-10 mx-auto mb-4 text-faint" />
                                            <h3 className="text-base font-semibold text-ink mb-1">Nothing to preview yet</h3>
                                            <p className="text-sm text-mutedink">Write in Edit mode, or generate a draft.</p>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="relative rounded-xl border border-line bg-surface overflow-hidden focus-within:border-accent transition-colors">
                                    <MarkdownToolbar textareaRef={textareaRef} value={content} onChange={setContent} />
                                    <textarea
                                        ref={textareaRef}
                                        value={content}
                                        onChange={(e) => setContent(e.target.value)}
                                        placeholder={`Write the ${typeLabel} in Markdown…`}
                                        aria-label={`${currentContentType?.label} (Markdown)`}
                                        className="w-full h-[calc(100vh-16rem)] min-h-[420px] px-8 py-6 font-mono text-sm leading-relaxed resize-none bg-surface text-ink placeholder:text-faint border-none outline-none"
                                    />
                                    {!content && !generating && (
                                        <div className="absolute inset-0 top-12 flex items-center justify-center pointer-events-none">
                                            <div className="text-center">
                                                <FileText className="w-10 h-10 mx-auto mb-3 text-faint" />
                                                <p className="text-sm text-mutedink">Start writing, or use <span className="text-ink font-medium">Generate {typeLabel}</span>.</p>
                                            </div>
                                        </div>
                                    )}
                                    {generating && (
                                        <div className="absolute inset-0 top-12 flex items-center justify-center bg-surface/70 backdrop-blur-[1px]">
                                            <p className="text-sm text-read flex items-center gap-2">
                                                <span className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                Generating the {typeLabel}…
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {!focusMode && (
                            <p className="mt-4 text-xs text-faint">
                                Markdown supported · ⌘/Ctrl + S to save · ⌘/Ctrl + P to preview
                            </p>
                        )}

                        {!focusMode && (
                            <RefineChatPanel
                                courseId={courseId}
                                domain={domain}
                                concept={concept}
                                contentType={contentType}
                                currentContent={content}
                                onApply={(newContent) => setContent(newContent)}
                            />
                        )}
                    </main>
                </div>
            </div>
        </div>
    );
}
